import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import AccountTreeIcon from "@mui/icons-material/AccountTree";

import PageHeader from "../../../../../components/PageHeader";
import useRoutePlant from "../../../../../hooks/useRoutePlant";
import { getAllData } from "../api";
import {
  buildDashboardLines,
  getVesselTag,
  latestRevision,
  procurementAliases,
} from "./buildDashboardLines";
import {
  extractInvoiceEvents,
  extractInvoices,
  linkProcurement,
} from "./procurementLink";
import { buildSampleEntries, extractLedger, summarizeLedger } from "./ledger";
import { saveLedger, saveSwitchPolicy } from "./ledgerApi";
import { boardEvents, computeBoard, hasAnyMeasure } from "./board";
import { DEMO_LINES, DEMO_REVISION, DEMO_TAG } from "./demoData";
import { buildSCurve } from "./sCurve";
import { fetchPlantProcurement } from "./procurementApi";
import { fetchPlantTasks } from "./switchApi";
import {
  DEFAULT_SWITCH_POLICY,
  buildDemoSwitchSchedule,
  computeSwitchSchedule,
  extractSwitchPolicy,
  matchSwitchTasks,
  switchComplianceFlags,
} from "./switchLink";
import { money } from "./format";
import { KpiCards, VarianceCards } from "./DashboardKpis";
import DashboardCharts from "./DashboardCharts";
import DashboardTable from "./DashboardTable";
import TableToolbar, { DEFAULT_VISIBLE, TOGGLES } from "./TableToolbar";
import LedgerDialog from "./LedgerDialog";
import SwitchPolicyDialog from "./SwitchPolicyDialog";
import BusinessFlowDialog from "./BusinessFlowDialog";

const SOURCE_LABEL = {
  procurement: "MRQs/POs",
  payments: "payments",
  invoices: "invoices",
};

// Why the demo estimate is showing, by how the estimate load ended.
const DEMO_REASON = {
  "no-project": "no project is selected",
  error: "the estimate couldn’t be loaded",
};

function Field({ label, children }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        fontWeight={700}
        color="text.secondary"
        sx={{ textTransform: "uppercase", letterSpacing: 0.4, display: "block" }}
      >
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600} noWrap>
        {children}
      </Typography>
    </Box>
  );
}

function StatePanel({ title, children }) {
  return (
    <Paper
      variant="outlined"
      sx={{ py: 8, px: 2, textAlign: "center", display: "grid", gap: 1 }}
    >
      <Typography variant="h6">{title}</Typography>
      {children}
    </Paper>
  );
}

/**
 * 5D cost dashboard for one saved PV Input Data revision:
 * /plant/:plantId/5d-dashboard?projectId=&projectName=[&revision=].
 * It is the default page for a Pressure Vessel selected in the sidebar tree,
 * which names no revision - the latest saved one is then used - and is also
 * opened for a specific revision from the "5D Dashboard" node under it.
 *
 * Estimate comes from the revision's saved Estimation summary (DataGridView8),
 * read straight from project/component/:id/PV/all - the same record
 * PVContext.savePVData writes - so unsaved edits in the Estimation tab show up
 * only after a Save. Actual / Paid / Invoiced come from the plant's
 * procurement + MIS records (see procurementLink.js), plus entered data.
 *
 * DEMO FALLBACK. Where the database has nothing to show, the page shows demo
 * data instead of an empty screen, and always says so:
 *  - no saved estimate (no project, revision not saved, empty estimate): the
 *    V201 demo table (demoData.js), with demo Budget / Actual / Invoiced / Paid;
 *  - a saved estimate but nothing at all recorded for those four: demo figures
 *    derived from that estimate.
 * Real and demo are never mixed - anything real switches the demo off for that
 * part - and demo data is never saved. A failed load is NOT treated as "no
 * data": it shows the error, with an explicit button to view the demo instead.
 */
export default function PvFiveDDashboardPage() {
  const { plant, plantId } = useRoutePlant();
  const [searchParams] = useSearchParams();

  const projectId = searchParams.get("projectId");
  const projectName = searchParams.get("projectName") || "";
  // A link may name no revision (opening the vessel from the tree does not):
  // the latest saved one is used, and `revision` below is what was resolved.
  const revisionParam = String(searchParams.get("revision") ?? "").trim();

  const [load, setLoad] = useState({ status: "loading", entry: null, revision: null });
  const [visible, setVisible] = useState(DEFAULT_VISIBLE);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [policyDialogOpen, setPolicyDialogOpen] = useState(false);
  const [flowDialogOpen, setFlowDialogOpen] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "" });
  // Set while entries are on screen but not (yet) saved to the MIS record.
  const [saveWarning, setSaveWarning] = useState(null);
  // After a failed load, the user can choose to look at the demo instead.
  const [forceDemo, setForceDemo] = useState(false);

  useEffect(() => {
    setForceDemo(false);
    if (!projectId) {
      setLoad({ status: "no-project", entry: null, revision: null });
      return undefined;
    }

    let cancelled = false;
    setLoad({ status: "loading", entry: null, revision: null });

    getAllData(projectId)
      .then((res) => {
        if (cancelled) return;
        const inputData = res?.all?.inputData;
        const resolved = revisionParam || latestRevision(inputData) || "0";
        const entry = inputData?.[resolved];
        setLoad(
          entry && typeof entry === "object"
            ? { status: "ready", entry, revision: resolved }
            : { status: "no-revision", entry: null, revision: resolved },
        );
      })
      .catch((error) => {
        console.error("Failed to load PV 5D dashboard data:", error);
        if (!cancelled) setLoad({ status: "error", entry: null, revision: null });
      });

    return () => {
      cancelled = true;
    };
  }, [projectId, revisionParam]);

  const revision = load.revision ?? (revisionParam || "0");

  // Plant-level procurement + invoices load on their own, so the estimate is
  // on screen without waiting for the (N-request) PO fan-out.
  const [proc, setProc] = useState({ status: "loading", data: null });

  useEffect(() => {
    if (!plantId) {
      setProc({ status: "idle", data: null });
      return undefined;
    }

    let cancelled = false;
    setProc({ status: "loading", data: null });

    fetchPlantProcurement(plantId)
      .then((data) => {
        if (!cancelled) setProc({ status: "ready", data });
      })
      .catch((error) => {
        console.error("Failed to load procurement for 5D dashboard:", error);
        if (!cancelled) {
          setProc({
            status: "ready",
            data: { failed: ["procurement", "payments", "invoices"] },
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [plantId]);

  // Plant tasks, for the switch model (switchLink.js): a line's switch fires
  // once the task matched to it (by WBS code, same as procurement) reaches
  // 100% / Completed. Its own fetch, independent of procurement, so one
  // failing doesn't block the other.
  const [taskState, setTaskState] = useState({ status: "loading", tasks: [] });

  useEffect(() => {
    if (!plantId) {
      setTaskState({ status: "idle", tasks: [] });
      return undefined;
    }

    let cancelled = false;
    setTaskState({ status: "loading", tasks: [] });

    fetchPlantTasks(plantId)
      .then((tasks) => {
        if (!cancelled) setTaskState({ status: "ready", tasks });
      })
      .catch((error) => {
        console.error("Failed to load tasks for the 5D dashboard:", error);
        if (!cancelled) setTaskState({ status: "ready", tasks: [], failed: true });
      });

    return () => {
      cancelled = true;
    };
  }, [plantId]);

  // Dated cost entries (see ledger.js). They ride in on the same MIS record the
  // invoices come from, so they are derived straight from it - no copy that can
  // lag behind a revision change or flash empty. Edits made on this page are
  // held as an override, tagged with the plant / project / revision they belong
  // to, so a stale one is ignored the moment any of those changes.
  const ledgerKey = `${plantId}|${projectId}|${revision}`;
  const savedLedger = useMemo(
    () =>
      proc.status === "ready"
        ? extractLedger(proc.data?.mis, projectId, revision)
        : [],
    [proc, projectId, revision],
  );
  const [localLedger, setLocalLedger] = useState({ key: null, entries: [] });
  const ledger = localLedger.key === ledgerKey ? localLedger.entries : savedLedger;
  const setLedger = (entries) => setLocalLedger({ key: ledgerKey, entries });

  // Saving merges into the MIS record, so it needs a clean read of it first.
  const canEnterData = proc.status === "ready" && !proc.data?.failed?.includes("invoices");

  const handleSaveLedger = async (entries) => {
    await saveLedger({ plantId, projectId, revision, entries });
    setLedger(entries);
    setSaveWarning(null);
    setSnack({ open: true, message: "Saved to the plant MIS record" });
  };

  // The switch model's client/vendor payment-lag policy - same storage slot
  // and the same local-override-until-saved pattern as the ledger above.
  const savedSwitchPolicy = useMemo(
    () =>
      proc.status === "ready"
        ? extractSwitchPolicy(proc.data?.mis, projectId, revision)
        : DEFAULT_SWITCH_POLICY,
    [proc, projectId, revision],
  );
  const [localPolicy, setLocalPolicy] = useState({ key: null, policy: null });
  const switchPolicy =
    localPolicy.key === ledgerKey ? localPolicy.policy : savedSwitchPolicy;

  const handleSavePolicy = async (policy) => {
    await saveSwitchPolicy({ plantId, projectId, revision, policy });
    setLocalPolicy({ key: ledgerKey, policy });
    setSnack({ open: true, message: "Saved to the plant MIS record" });
  };

  // ---- 1. The estimate: real, or the demo table when nothing is saved --------
  const realLines = useMemo(() => buildDashboardLines(load.entry), [load.entry]);

  const demoEstimate =
    load.status === "no-project" ||
    load.status === "no-revision" ||
    (load.status === "ready" && realLines.length === 0) ||
    (load.status === "error" && forceDemo);

  const baseLines = demoEstimate ? DEMO_LINES : realLines;
  const tag = demoEstimate ? DEMO_TAG : getVesselTag(load.entry);

  // ---- 2. What the database holds for the four measures (never with demo) ---
  const useReal = !demoEstimate;

  const linked = useMemo(() => {
    const data = proc.data;
    if (
      !useReal ||
      proc.status !== "ready" ||
      !data ||
      data.failed?.includes("procurement")
    ) {
      return null;
    }
    return linkProcurement({
      mrqs: data.mrqs,
      posByMrqId: data.posByMrqId,
      payments: data.payments,
      paymentsLoaded: data.paymentsLoaded,
      aliases: procurementAliases(baseLines),
    });
  }, [useReal, proc, baseLines]);

  const invoices = useMemo(
    () => (useReal ? extractInvoices(proc.data?.mis) : {}),
    [useReal, proc.data],
  );
  const ledgerSummary = useMemo(
    () => summarizeLedger(useReal ? ledger : []),
    [useReal, ledger],
  );
  const realBoard = useMemo(
    () => computeBoard({ baseLines, linked, invoices, ledgerSummary }),
    [baseLines, linked, invoices, ledgerSummary],
  );

  // ---- 3. Demo figures, only when there is nothing real for those four ------
  const demoMeasures =
    demoEstimate ||
    (proc.status === "ready" &&
      ledger.length === 0 &&
      !hasAnyMeasure(realBoard.totals));

  const demoSummary = useMemo(
    () => (demoMeasures ? summarizeLedger(buildSampleEntries(baseLines)) : null),
    [demoMeasures, baseLines],
  );

  const { lines, totals, vars } = useMemo(
    () =>
      demoMeasures
        ? computeBoard({
            baseLines,
            linked: null,
            invoices: {},
            ledgerSummary: demoSummary,
          })
        : realBoard,
    [demoMeasures, baseLines, demoSummary, realBoard],
  );
  const shownInvoices = demoMeasures ? {} : invoices;

  const curve = useMemo(
    () =>
      buildSCurve(
        demoMeasures
          ? boardEvents({
              linked: null,
              invoiceEvents: [],
              ledgerSummary: demoSummary,
            })
          : boardEvents({
              linked,
              invoiceEvents: extractInvoiceEvents(proc.data?.mis),
              ledgerSummary,
            }),
      ),
    [demoMeasures, demoSummary, linked, ledgerSummary, proc.data],
  );

  // ---- 4. The switch model: cash flow, from tasks matched to lines ----------
  // Independent of demoMeasures above: switches come from Tasks, not from the
  // ledger/procurement, so "nothing recorded for bud/act/inv/pd" doesn't mean
  // "no switch data" (and real tasks existing doesn't fill those either). Only
  // demoEstimate (no real vessel at all) switches this to a demo schedule.
  const switchByCode = useMemo(() => {
    if (demoEstimate || taskState.status !== "ready") return new Map();
    return matchSwitchTasks(baseLines, taskState.tasks);
  }, [demoEstimate, taskState, baseLines]);

  const switchSchedule = useMemo(
    () =>
      demoEstimate
        ? buildDemoSwitchSchedule(baseLines)
        : computeSwitchSchedule(baseLines, switchByCode, switchPolicy),
    [demoEstimate, baseLines, switchByCode, switchPolicy],
  );
  // Needs a real Paid amount (from POs, on realBoard.lines - never the
  // demoMeasures-substituted `lines`, which can carry a synthetic Paid amount
  // on every line and would flag against numbers nobody actually entered).
  const complianceFlags = useMemo(
    () =>
      demoEstimate || demoMeasures
        ? []
        : switchComplianceFlags(realBoard.lines, switchSchedule),
    [demoEstimate, demoMeasures, realBoard, switchSchedule],
  );
  // Whether it's meaningful to say "nothing's been paid ahead of its switch" -
  // same guard as complianceFlags, so the green confirmation never shows for
  // demo-substituted Paid amounts either.
  const anyRealPaid =
    !demoEstimate &&
    !demoMeasures &&
    realBoard.lines.some((line) => line.pc != null && line.pc > 0);

  // Real data exists for some of Invoiced but nothing else (e.g. MIS invoices
  // only), so no demo - offer to save a sample for the rest instead.
  const showSampleCta =
    !demoMeasures &&
    ledger.length === 0 &&
    !totals.has.bud &&
    !totals.has.act &&
    !totals.has.pd;

  // One click from that state. The sample is put on screen first, so the
  // numbers show even when the MIS record can't take the write; saving is then
  // attempted, and if it fails the page says so instead of pretending.
  const handleLoadSample = async () => {
    const entries = [
      ...ledger.filter((entry) => entry.source !== "sample"),
      ...buildSampleEntries(baseLines),
    ];
    setLedger(entries);

    if (!canEnterData) {
      setSaveWarning(
        "Showing sample data, but the plant’s MIS record couldn’t be read, so it isn’t saved and will be gone on reload.",
      );
      return;
    }
    try {
      await handleSaveLedger(entries);
    } catch (error) {
      console.error("Failed to save sample 5D entries:", error);
      setSaveWarning(
        "Showing sample data, but it couldn’t be saved to the plant’s MIS record, so it will be gone on reload.",
      );
    }
  };

  const handleRetrySave = async () => {
    try {
      await handleSaveLedger(ledger);
    } catch (error) {
      console.error("Retry saving 5D entries failed:", error);
      setSnack({ open: true, message: "Still couldn’t save — try again in a moment" });
    }
  };

  const show = useMemo(
    () =>
      Object.fromEntries(
        TOGGLES.map(({ value }) => [value, visible.includes(value)]),
      ),
    [visible],
  );

  const failed = demoEstimate ? [] : proc.data?.failed || [];
  const procStatusText = (() => {
    if (demoEstimate) return "demo data";
    if (proc.status === "loading") return "loading…";
    if (proc.status !== "ready" || !proc.data) return "—";
    const parts = linked
      ? [
          `${linked.counts.mrqs} MRQ`,
          `${linked.counts.pos} PO`,
          `${linked.counts.payments} payments`,
        ]
      : [];
    if (failed.length) {
      parts.push(`unavailable: ${failed.map((f) => SOURCE_LABEL[f]).join(", ")}`);
    }
    return parts.join(" · ") || "no data";
  })();

  const docUid = demoEstimate
    ? DEMO_REVISION.documentUID
    : load.entry?.documentUID || `Rev ${revision}`;
  const wbsName = projectName || (demoEstimate ? "Demo" : projectId) || "PV project";

  const demoReason =
    DEMO_REASON[load.status] ||
    (load.status === "no-revision"
      ? `revision ${revision} isn’t saved`
      : "no estimate is saved for this revision");

  const renderBody = () => {
    if (load.status === "loading") {
      return (
        <StatePanel title="Loading estimate…">
          <Box>
            <CircularProgress size={24} />
          </Box>
        </StatePanel>
      );
    }
    if (load.status === "error" && !demoEstimate) {
      return (
        <StatePanel title="Couldn't load this revision">
          <Typography variant="body2" color="text.secondary">
            Check your connection and reopen the dashboard.
          </Typography>
          <Box>
            <Button variant="outlined" size="small" onClick={() => setForceDemo(true)}>
              Show demo data instead
            </Button>
          </Box>
        </StatePanel>
      );
    }

    return (
      <>
        {demoEstimate && (
          <Alert severity="warning" variant="filled">
            <b>Demo data</b> — {demoReason}, so the {DEMO_TAG} sample table is
            shown with illustrative Budget, Actual, Invoiced and Paid. Nothing
            here is saved.
          </Alert>
        )}
        {demoMeasures && !demoEstimate && (
          <Alert
            severity="warning"
            variant="outlined"
            action={
              <Button
                size="small"
                variant="contained"
                disabled={!canEnterData}
                onClick={handleLoadSample}
              >
                Save to MIS
              </Button>
            }
          >
            <b>Demo figures</b> — nothing has been recorded for Budget, Actual,
            Invoiced or Paid, so illustrative ones are shown, built from this
            estimate. “Save to MIS” stores exactly these in the plant’s MIS
            record (marked as sample, removable under “Enter data”), so they
            show as recorded data. Or use “Enter data” to record real ones;
            they replace these.
          </Alert>
        )}
        {saveWarning && (
          <Alert
            severity="warning"
            variant="outlined"
            action={
              canEnterData ? (
                <Button color="inherit" size="small" onClick={handleRetrySave}>
                  Retry save
                </Button>
              ) : null
            }
          >
            {saveWarning}
          </Alert>
        )}
        {showSampleCta && (
          <Alert
            severity="info"
            variant="outlined"
            action={
              <Stack direction="row" spacing={1}>
                <Button size="small" variant="contained" onClick={handleLoadSample}>
                  Load sample data
                </Button>
                <Button
                  size="small"
                  disabled={!canEnterData}
                  onClick={() => setDialogOpen(true)}
                >
                  Enter data
                </Button>
              </Stack>
            }
          >
            Budget, Actual and Paid are empty — nothing has been entered, and no
            POs or payments link to this vessel yet. Load sample data to see the
            dashboard filled in, or enter your own.
          </Alert>
        )}

        <KpiCards totals={totals} lineCount={lines.length} />
        <VarianceCards vars={vars} />
        <DashboardCharts
          lines={lines}
          totals={totals}
          curve={curve}
          switchSchedule={switchSchedule}
          complianceFlags={complianceFlags}
          anyPaid={anyRealPaid}
          onEditPolicy={() => setPolicyDialogOpen(true)}
          policyDisabled={
            demoEstimate
              ? "This is demo data — save an estimate for this revision first."
              : proc.status === "loading"
                ? "Still loading the plant’s MIS record."
                : !canEnterData
                  ? "The plant’s MIS record couldn’t be read, so changes can’t be saved safely."
                  : null
          }
        />
        <DashboardTable
          lines={lines}
          totals={totals}
          invoices={shownInvoices}
          show={show}
          tag={tag}
          toolbar={
            <TableToolbar
              visible={visible}
              onVisibleChange={setVisible}
              onEnterData={() => setDialogOpen(true)}
              enterDisabled={demoEstimate || !canEnterData || load.status !== "ready"}
              enterDisabledReason={
                demoEstimate
                  ? "This is demo data — save an estimate for this revision first."
                  : proc.status === "loading"
                    ? "Still loading the plant’s MIS record."
                    : "The plant’s MIS record couldn’t be read, so entries can’t be saved safely."
              }
            />
          }
        />

        <Alert severity="info" variant="outlined" sx={{ alignItems: "flex-start" }}>
          <Box component="ul" sx={{ m: 0, pl: 2, "& li": { mb: 0.25 } }}>
            <li>
              <b>Estimate</b> is the saved Estimation summary for this revision.
            </li>
            <li>
              <b>Actual / Paid</b> come from the plant’s MRQ and PO records,
              matched to the COP / CPS lines by WBS code (PO unit cost × procured
              qty; each PO’s payments are split across its lines by cost share).
            </li>
            <li>
              <b>Invoiced</b> is the plant MIS total per discipline, so it shows
              on the category rows.
            </li>
            <li>
              <b>Budget</b>, and anything not yet in a PO or the MIS, comes from
              “Enter data” (saved in the plant’s MIS record and added to the
              figures above). Variances only cover categories where both sides
              exist.
            </li>
            <li>
              <b>Cash flow</b>, in its own panel below, is separate from all of
              this: a line’s work is done when its matched task hits 100% /
              Completed (man-hour lines in packets, as progress covers them);
              it “switches” once the client approves it and is billed at cost
              plus the policy markup. The client-pay / vendor-pay dates that
              follow are projected by policy, not recorded — see “Cash flow —
              the switch”.
            </li>
          </Box>
        </Alert>

        {ledger.length > 0 && !demoEstimate && (
          <Alert severity="info" variant="outlined">
            {ledger.length} entered {ledger.length === 1 ? "entry" : "entries"}{" "}
            included
            {ledger.some((entry) => entry.source === "sample")
              ? ` (${ledger.filter((entry) => entry.source === "sample").length} are sample data — remove them under “Enter data”)`
              : ""}
            .
          </Alert>
        )}

        {linked && (linked.scopedCost > 0 || linked.plantWideCost > 0) && (
          <Alert severity="info" variant="outlined">
            Linked procurement: {money(linked.scopedCost)} matched by this
            vessel’s codes{tag ? ` (${tag}.…)` : ""}, {money(linked.plantWideCost)}{" "}
            by plain codes (2.1 …), which are plant-wide and can include other
            vessels in {plant?.name || "this plant"}.
          </Alert>
        )}
        {linked?.unlinkedCost > 0 && (
          <Alert severity="warning" variant="outlined">
            {money(linked.unlinkedCost)} of PO cost in this plant isn’t tied to a
            WBS line here (no matching code) and is left out.
          </Alert>
        )}
        {!tag && (
          <Alert severity="warning" variant="outlined">
            No vessel tag saved on the Design tab, so internal codes (1.1, 2.1 …)
            are shown instead of V201.1 … and procurement can only match plain
            codes.
          </Alert>
        )}
        {failed.length > 0 && (
          <Alert severity="warning" variant="outlined">
            Couldn’t load {failed.map((f) => SOURCE_LABEL[f]).join(", ")} — those
            columns show “—”.
          </Alert>
        )}
        {taskState.failed && !demoEstimate && (
          <Alert severity="warning" variant="outlined">
            Couldn’t load the plant’s tasks — no line can be shown as switched
            until that loads.
          </Alert>
        )}
      </>
    );
  };

  return (
    <Box>
      <PageHeader plant={plant} pageTitle="5D Dashboard" moduleKey="pv" />
      <Box sx={{ p: 2, display: "grid", gap: 1.5 }}>
        <Paper variant="outlined" sx={{ p: 1.5 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            useFlexGap
            spacing={1.5}
          >
            <Stack
              direction="row"
              spacing={3}
              useFlexGap
              flexWrap="wrap"
              alignItems="center"
            >
              <Field label="WBS">{wbsName}</Field>
              <Field label="Vessel tag">{tag || "—"}</Field>
              <Field label="Rev.">{revision}</Field>
              <Field label="Document">{docUid}</Field>
              <Field label="Procurement · Invoices">{procStatusText}</Field>
            </Stack>
            <Tooltip title="A plain-language explanation of how a line's cost becomes client billing, then a vendor/payroll payment">
              <Button
                size="small"
                variant="outlined"
                startIcon={<AccountTreeIcon fontSize="small" />}
                onClick={() => setFlowDialogOpen(true)}
              >
                How money flows
              </Button>
            </Tooltip>
          </Stack>
        </Paper>
        {renderBody()}
      </Box>

      <LedgerDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        entries={ledger}
        lines={baseLines}
        onSave={handleSaveLedger}
      />
      <SwitchPolicyDialog
        open={policyDialogOpen}
        onClose={() => setPolicyDialogOpen(false)}
        policy={switchPolicy}
        lines={baseLines}
        onSave={handleSavePolicy}
      />
      <BusinessFlowDialog
        open={flowDialogOpen}
        onClose={() => setFlowDialogOpen(false)}
        policy={switchPolicy}
        complianceFlags={complianceFlags}
        anyPaid={anyRealPaid}
        demo={demoEstimate}
      />
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
